import React, { useState, useEffect } from 'react';
import RoleBasedNavigation from '../../components/navigation/RoleBasedNavigation';
import NavigationBreadcrumbs from '../../components/navigation/NavigationBreadcrumbs';
import FinancialHealthScore from './components/FinancialHealthScore';
import MetricsCard from './components/MetricsCard';




import AIRecommendationsPanel from './components/AIRecommendationsPanel';
import QuickActions from './components/QuickActions';
import GoalTracker from './components/GoalTracker';

import ARModeViewer from '../../components/ARModeViewer';
import { useAuth } from '../../contexts/AuthContext';
import { transactionsService, budgetsService, goalsService } from '../../services/financialDataService';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import SkeletonLoader from '../../components/ui/SkeletonLoader';


const RetailUserDashboard = () => {
  const { user } = useAuth();
  const [healthScore] = useState({
    score: 78,
    trend: 'up'
  });

  const [metrics, setMetrics] = useState([]);
  const [heatmapData] = useState([
    {
      name: 'Groceries',
      values: [45, 67, 52, 78, 89, 34, 56]
    },
    {
      name: 'Dining',
      values: [23, 45, 67, 34, 56, 78, 45]
    },
    {
      name: 'Transport',
      values: [56, 34, 45, 67, 23, 89, 67]
    },
    {
      name: 'Entertainment',
      values: [12, 23, 45, 56, 67, 34, 78]
    },
    {
      name: 'Shopping',
      values: [67, 45, 78, 23, 34, 56, 89]
    },
    {
      name: 'Utilities',
      values: [34, 56, 23, 45, 78, 67, 45]
    }
  ]);

  const [incomeData] = useState([
    { month: 'Aug', income: 5200, expenses: 3800 },
    { month: 'Sep', income: 5400, expenses: 4100 },
    { month: 'Oct', income: 5600, expenses: 4300 },
    { month: 'Nov', income: 5300, expenses: 3900 },
    { month: 'Dec', income: 6200, expenses: 4800 },
    { month: 'Jan', income: 5800, expenses: 4200 },
    { month: 'Feb', income: 5900, expenses: 4400 }
  ]);

  const [budgetCategories, setBudgetCategories] = useState([]);
  const [recommendations] = useState([
    {
      id: 1,
      title: 'Reduce Dining Expenses',
      description: 'You\'ve spent 93% of your dining budget. Consider meal prepping to save $120/month.',
      icon: 'Utensils',
      priority: 'high',
      impact: '$120/month',
      timeframe: 'Immediate'
    },
    {
      id: 2,
      title: 'Optimize Subscription Services',
      description: 'You have 8 active subscriptions totaling $156/month. Cancel unused services to save money.',
      icon: 'CreditCard',
      priority: 'medium',
      impact: '$78/month',
      timeframe: '1-2 weeks'
    },
    {
      id: 3,
      title: 'Increase Emergency Fund',
      description: 'Your emergency fund covers 4.2 months. Aim for 6 months to improve financial security.',
      icon: 'Shield',
      priority: 'low',
      impact: '$2,400 total',
      timeframe: '6-12 months'
    }
  ]);

  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showARMode, setShowARMode] = useState(false);

  // Fetch data from Supabase
  useEffect(() => {
    if (!user?.id) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch budgets
        const currentMonth = new Date()?.getMonth() + 1;
        const currentYear = new Date()?.getFullYear();
        const budgetsData = await budgetsService?.getAll(user?.id, currentMonth, currentYear);
        
        // Transform budgets to budget categories format
        const categoryMap = {
          groceries: { name: 'Groceries', icon: 'ShoppingCart', color: 'var(--color-success)' },
          dining: { name: 'Dining Out', icon: 'Utensils', color: 'var(--color-warning)' },
          transportation: { name: 'Transportation', icon: 'Car', color: 'var(--color-primary)' },
          entertainment: { name: 'Entertainment', icon: 'Film', color: 'var(--color-secondary)' }
        };

        const transformedBudgets = budgetsData?.map(budget => ({
          id: budget?.id,
          name: categoryMap?.[budget?.category]?.name || budget?.category,
          icon: categoryMap?.[budget?.category]?.icon || 'DollarSign',
          color: categoryMap?.[budget?.category]?.color || 'var(--color-primary)',
          spent: parseFloat(budget?.spentAmount || 0),
          budget: parseFloat(budget?.budgetAmount || 0),
          transactions: 0 // Would need to count from transactions
        }));
        setBudgetCategories(transformedBudgets);

        // Fetch goals
        const goalsData = await goalsService?.getAll(user?.id);
        const transformedGoals = goalsData?.map(goal => ({
          id: goal?.id,
          name: goal?.title,
          category: 'Savings',
          icon: 'Target',
          color: 'var(--color-success)',
          current: parseFloat(goal?.currentAmount || 0),
          target: parseFloat(goal?.targetAmount || 0),
          targetDate: goal?.targetDate,
          totalDays: goal?.targetDate ? Math.ceil((new Date(goal.targetDate) - new Date()) / (1000 * 60 * 60 * 24)) : 0,
          monthlyContribution: 0,
          probability: 85
        }));
        setGoals(transformedGoals);

        // Calculate metrics from transactions
        const transactions = await transactionsService?.getAll(user?.id);
        const totalIncome = transactions?.filter(t => t?.type === 'income')?.reduce((sum, t) => sum + parseFloat(t?.amount), 0);
        const totalExpenses = transactions?.filter(t => t?.type === 'expense')?.reduce((sum, t) => sum + Math.abs(parseFloat(t?.amount)), 0);
        const savingsRatio = totalIncome > 0 ? ((totalIncome - totalExpenses) / totalIncome * 100)?.toFixed(1) : 0;

        setMetrics([
          {
            id: 1,
            title: 'Savings Ratio',
            value: `${savingsRatio}%`,
            subtitle: savingsRatio >= 20 ? 'Above recommended 20%' : 'Below recommended 20%',
            icon: 'PiggyBank',
            trend: savingsRatio >= 20 ? 'up' : 'down',
            trendValue: '+2.3%',
            color: 'var(--color-success)'
          },
          {
            id: 2,
            title: 'Debt-to-Income',
            value: '32%',
            subtitle: 'Within healthy range',
            icon: 'TrendingDown',
            trend: 'down',
            trendValue: '-5%',
            color: 'var(--color-primary)'
          },
          {
            id: 3,
            title: 'Emergency Fund',
            value: '4.2 months',
            subtitle: 'Coverage duration',
            icon: 'Shield',
            trend: 'up',
            trendValue: '+0.5',
            color: 'var(--color-warning)'
          },
          {
            id: 4,
            title: 'Cash Runway',
            value: '127 days',
            subtitle: 'At current spending rate',
            icon: 'Clock',
            trend: 'up',
            trendValue: '+12',
            color: 'var(--color-secondary)'
          }
        ]);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    // Real-time subscriptions
    const budgetsChannel = supabase?.channel('budgets_changes')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'budgets',
          filter: `user_id=eq.${user?.id}`
        },
        () => {
          fetchData();
        }
      )?.subscribe();

    const goalsChannel = supabase?.channel('goals_changes')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'goals',
          filter: `user_id=eq.${user?.id}`
        },
        () => {
          fetchData();
        }
      )?.subscribe();

    const transactionsChannel = supabase?.channel('transactions_changes')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'transactions',
          filter: `user_id=eq.${user?.id}`
        },
        () => {
          fetchData();
        }
      )?.subscribe();

    return () => {
      supabase?.removeChannel(budgetsChannel);
      supabase?.removeChannel(goalsChannel);
      supabase?.removeChannel(transactionsChannel);
    };
  }, [user?.id]);

  const financialDataForAR = {
    healthScore: healthScore?.score,
    savingsRatio: '24.5%',
    debtToIncome: '32%',
    emergencyFund: '4.2 months',
    monthlyIncome: 5900,
    monthlyExpenses: 4400,
    topCategories: budgetCategories?.slice(0, 5)?.map(cat => ({
      name: cat?.name,
      spent: cat?.spent,
      budget: cat?.budget
    }))
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-fintech-blue-50 via-white to-fintech-purple-50">
      <RoleBasedNavigation role="retail-user" />
      <div className="container mx-auto px-4 py-6">
        <NavigationBreadcrumbs
          items={[
            { label: 'Home', path: '/' },
            { label: 'Retail Dashboard', path: '/retail-user-dashboard' }
          ]}
        />

        <div className="grid grid-cols-1 gap-6 mt-6">
          {loading ? (
            <SkeletonLoader variant="card" count={1} />
          ) : (
            <FinancialHealthScore score={healthScore?.score} trend={healthScore?.trend} loading={loading} />
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {loading ? (
              <>
                <SkeletonLoader variant="card" count={4} />
              </>
            ) : (
              metrics?.map((metric, index) => (
                <div key={metric?.id} className="animate-fade-in" style={{ animationDelay: `${index * 0.1}s` }}>
                  <MetricsCard {...metric} />
                </div>
              ))
            )}
          </div>

          {/* AR Feature Spotlight Section */}
          <div className="bg-gradient-to-br from-fintech-purple-500 via-fintech-blue-500 to-fintech-teal-500 rounded-3xl p-8 shadow-elevation-4 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-fintech-green-500/20 rounded-full blur-3xl"></div>
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center animate-float">
                  <Icon name="Camera" size={28} className="text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-2xl font-bold text-white">AR Financial Vision</h2>
                    <span className="px-3 py-1 bg-fintech-green-500 text-white text-xs font-bold rounded-full animate-pulse">NEW</span>
                  </div>
                  <p className="text-white/90 text-sm">Experience the future of financial decision-making</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <div className="flex items-center gap-3 mb-2">
                    <Icon name="Eye" size={20} className="text-white" />
                    <h3 className="text-white font-semibold">Real-Time Visualization</h3>
                  </div>
                  <p className="text-white/80 text-sm">Point your camera at products to see instant budget impact analysis</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <div className="flex items-center gap-3 mb-2">
                    <Icon name="Brain" size={20} className="text-white" />
                    <h3 className="text-white font-semibold">AI-Powered Insights</h3>
                  </div>
                  <p className="text-white/80 text-sm">Get smart recommendations and behavioral nudges in real-time</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <div className="flex items-center gap-3 mb-2">
                    <Icon name="Zap" size={20} className="text-white" />
                    <h3 className="text-white font-semibold">Instant Scenarios</h3>
                  </div>
                  <p className="text-white/80 text-sm">Simulate purchase impact on your financial goals instantly</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <div className="flex items-center gap-3 mb-2">
                    <Icon name="Target" size={20} className="text-white" />
                    <h3 className="text-white font-semibold">Smart Alternatives</h3>
                  </div>
                  <p className="text-white/80 text-sm">Discover budget-friendly alternatives with AR object detection</p>
                </div>
              </div>
              <button
                onClick={() => setShowARMode(true)}
                className="w-full md:w-auto px-8 py-4 bg-white text-fintech-purple-600 rounded-xl font-bold text-lg shadow-elevation-3 hover:shadow-elevation-5 hover:scale-105 transition-all duration-300 flex items-center justify-center gap-3"
              >
                <Icon name="Camera" size={24} />
                Launch AR Experience
                <Icon name="ArrowRight" size={20} />
              </button>
            </div>
          </div>

          <AIRecommendationsPanel financialData={financialDataForAR} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-6">
            <QuickActions />
          </div>
          <div className="space-y-6">
            {loading ? (
              <SkeletonLoader variant="card" count={1} />
            ) : (
              <GoalTracker goals={goals} loading={loading} />
            )}
          </div>
        </div>

        {/* AR Mode Viewer */}
        {showARMode && (
          <ARModeViewer
            budgetData={budgetCategories}
            financialData={financialDataForAR}
            onClose={() => setShowARMode(false)}
            userRole="retail"
          />
        )}
      </div>
    </div>
  );
};

export default RetailUserDashboard;